import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { ReactComponent as FavoriteSelectedIcon } from "assets/images/icons/icn_favorite_selected.svg";
import { ReactComponent as FavoriteUnselectedIcon } from "assets/images/icons/icn_favorite_unselected.svg";
import { ReactComponent as SettingsIcon } from "assets/images/icons/icn_btn_settings.svg";
import { ReactComponent as SettingsHoverIcon } from "assets/images/icons/icn_btn_settings_hover.svg";
import { setCameraDragData } from "../../lib/cameraDrag";

const STATUS_ONLINE = "#00B61B";
const STATUS_OFFLINE = "#DB0046";

/**
 * Camera icon with Online (green) / Offline (red) status light — matches UX badge on icon.
 */
const CameraStatusIcon = ({ online }) => {
  const theme = useTheme();
  return (
    <Box sx={{ position: "relative", width: 24, height: 24, flexShrink: 0 }}>
      <VideocamOutlinedIcon
        sx={{
          fontSize: 24,
          color: online ? theme.palette.primary.main : theme.palette.grey[400],
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 1,
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: online ? STATUS_ONLINE : STATUS_OFFLINE,
          border: "1.5px solid #fff",
          boxSizing: "border-box",
        }}
      />
    </Box>
  );
};

/**
 * DeviceItem
 * Renders a single camera/device row in the Device section.
 *
 * Props:
 *   device      - { id, name, favorited?, linkStatus? }
 *   onSettings  - called with device when the settings icon is clicked
 *   onPlay      - called with device when the row (name area) is clicked
 */
const DeviceItem = ({ device, isFavorited, onToggleFavorite, onSettings, onPlay }) => {
  const theme = useTheme();
  const [settingsHover, setSettingsHover] = useState(false);
  const [favoriteHover, setFavoriteHover] = useState(false);
  const [isSelected, setIsSelected] = useState(Boolean(isFavorited));
  const online = device?.linkStatus === "online";

  useEffect(() => {
    setIsSelected(Boolean(isFavorited));
  }, [isFavorited]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "4px",
        py: "2px",
        cursor: onPlay ? "pointer" : "default",
        borderRadius: "4px",
        "&:hover": onPlay ? { backgroundColor: "rgba(23,38,100,0.06)" } : undefined,
      }}
    >
      {/* Left: camera icon + name — click to play */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "6px", p: "4px", flex: 1, minWidth: 0 }}
        draggable
        onDragStart={(e) => setCameraDragData(e, device)}
        onClick={() => onPlay?.(device)}
      >
        <CameraStatusIcon online={online} />
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: online ? theme.palette.primary.main : theme.palette.grey[500],
          }}
        >
          {device.name}
        </Typography>
      </Box>

      {/* Right: favorite + settings */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsSelected((prev) => !prev);
            onToggleFavorite?.(device.id);
          }}
          onMouseEnter={() => setFavoriteHover(true)}
          onMouseLeave={() => setFavoriteHover(false)}
          disableRipple
          sx={{
            p: "4px",
            backgroundColor: favoriteHover ? "rgba(23,38,100,0.08)" : "transparent",
            transition: "all 0.2s",
            borderRadius: "4px",
          }}
        >
          {isSelected ? (
            <FavoriteSelectedIcon width={20} height={20} />
          ) : (
            <FavoriteUnselectedIcon width={20} height={20} style={{ opacity: favoriteHover ? 1 : 0.6 }} />
          )}
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onSettings?.(device);
          }}
          onMouseEnter={() => setSettingsHover(true)}
          onMouseLeave={() => setSettingsHover(false)}
          disableRipple
          sx={{
            p: "4px",
            backgroundColor: settingsHover ? "rgba(23,38,100,0.08)" : "transparent",
            transition: "all 0.2s",
            borderRadius: "4px",
            color: theme.palette.primary.main,
          }}
        >
          <SettingsIcon width={16} height={16} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DeviceItem;
export { CameraStatusIcon };
